/* eslint-disable i18next/no-literal-string */
/* eslint-disable @typescript-eslint/no-unused-vars */
// MUI imports
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material';
import { useApiMutation } from '@/hooks/useApi';
import { useProfile } from '@/hooks/useProfile';
import { ProfileImage, UserProfile } from '@/interface/profile';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { extractMemberKeys } from '@/util/profileKeys';
import { writeStaffProfile } from '@/util/staffUtils';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useGlobalToast } from '@/providers/globalToastProvider';

// Define the data structure for a staff record
// interface StaffMember {
//   id: string;
//   firstName: string;
//   lastName: string;
//   agentId: string;
//   uuid: string;
//   email: string;
//   status: 'Active' | 'Inactive';
//   teamType?: string;
//   teamName?: string;
//   teamLead: boolean;
//   state: string;
//   country: string;
// }

interface StaffDataTableProps {
  data: any[];
  loading?: boolean;
  /** When set, immediately opens the agent with this UUID (used for cross-app deep link). */
  autoLoadAgent?: {
    uuid: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  } | null;
  onAutoLoadDone?: () => void;
}

/**
 * Build the impersonated user's profile from a `/staff/agents` row.
 *
 * `MemberStateOrProvince`, `MemberKeyNumeric` and `TeamKeyNumeric` are on the
 * `/chat_stream` payload, so they have to be carried through here — staff mode
 * never calls `/profile`, and `ProfileProvider` hydrates this object verbatim
 * from `localStorage.profileStaff`.
 *
 * `/profile-image` is the authoritative source for the numeric keys (it is what
 * the non-staff path uses); the agent row is the fallback.
 */
export const mapAgentToUserProfile = (
  agent: any,
  profileImageRes?: ProfileImage | null,
): UserProfile => {
  const member = agent?.Member || {};
  const { memberKeyNumeric, teamKeyNumeric } =
    extractMemberKeys(profileImageRes);
  return {
    value: [
      {
        MemberFirstName: member.MemberFirstName || '',
        MemberLastName: member.MemberLastName || '',
        MemberEmail: member.MemberEmail || '',
        MemberMobilePhone: member.MemberMobilePhone || '',
        MemberCity: member.MemberCity || '',
        MemberCountry: member.MemberCountry || '',
        MemberStateOrProvince: member.MemberStateOrProvince,
        MemberKeyNumeric: memberKeyNumeric ?? member.MemberKeyNumeric,
        TeamKeyNumeric: teamKeyNumeric ?? member.TeamKeyNumeric,
        eXpSystemKey: agent.eXpSystemKey,
        eXpMember: {
          TeamLead: agent.TeamLead || false,
          Uuid: agent.Uuid || '',
        },
      },
    ],
  };
};

const StaffDataTable = ({
  data,
  loading = false,
  autoLoadAgent,
  onAutoLoadDone,
}: StaffDataTableProps) => {
  const { showToast } = useGlobalToast();
  const profileImageMutation = useApiMutation<ProfileImage, { userId: string }>(
    {
      url: '/profile-image',
      method: 'post',
      options: { retry: 1 },
      endpoint: 'prod',
    },
  );
  const imagesMutation = useApiMutation<string, { url: string }>({
    url: '/images',
    method: 'post',
    options: { retry: false },
    endpoint: 'prod',
  });
  const featureToggleMutation = useApiMutation<
    { expires: string; keys: string[] },
    { userId: string }
  >({
    url: '/feature-toggles',
    method: 'post',
    endpoint: 'prod',
  });
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { setProfile, setProfileImage } = useProfile();
  const [platformLoading, setPlatformLoading] = useState(false);
  const autoLoadFiredRef = useRef(false);
  const featureFlagsEnabled = process.env.REACT_APP_FEATURE_TOGGLE_ENABLED;
  const handleActionClick = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const openAgentPlatformByData = async (agent: any) => {
    setPlatformLoading(true);

    try {
      if (featureFlagsEnabled === 'true') {
        const featureFlagsRes = await featureToggleMutation.mutateAsync({
          userId: agent.Uuid,
        });
        const isAgentPlatformEnabled = featureFlagsRes?.keys?.includes(
          FEATURE_FLAGS.ENABLE_AGENT_PLATFORM,
        );

        if (!isAgentPlatformEnabled) {
          showToast({
            message: 'This user is not able to login',
            severity: 'warning',
            positionVertical: 'top',
            positionHorizontal: 'center',
          });
          handleMenuClose();
          return;
        }
      }

      const profileImageRes: ProfileImage =
        await profileImageMutation.mutateAsync({
          userId: agent.Uuid,
        });

      const agentPhotoUrl = profileImageRes?.value?.[0]?.AgentPhotoUrl;
      let imagesRes = '';
      try {
        imagesRes = await imagesMutation.mutateAsync({
          url: agentPhotoUrl || '',
        });
      } catch (error) {
        console.error('Error in imagesMutation:', error);
      }

      setProfileImage(imagesRes || '');
      localStorage.setItem('profileStaffImage', JSON.stringify(imagesRes));

      const mappedProfile = mapAgentToUserProfile(agent, profileImageRes);
      setProfile(mappedProfile);
      writeStaffProfile(mappedProfile);

      navigate('/chat');
      handleMenuClose();
    } catch (error) {
      console.error('Error in openAgentPlatformByData:', error);
      showToast({
        message: 'This user is not able to login',
        severity: 'warning',
        positionVertical: 'top',
        positionHorizontal: 'center',
      });
      handleMenuClose();
    } finally {
      setPlatformLoading(false);
    }
  };

  // Auto-load agent coming from a cross-app deep link (my-exp-plus → Mira).
  // Uses a ref guard so it fires exactly once even under StrictMode double-invoke.
  useEffect(() => {
    if (!autoLoadAgent?.uuid || autoLoadFiredRef.current) return;
    autoLoadFiredRef.current = true;

    const syntheticAgent = {
      Uuid: autoLoadAgent.uuid,
      Member: {
        MemberFirstName: autoLoadAgent.firstName ?? '',
        MemberLastName: autoLoadAgent.lastName ?? '',
        MemberEmail: autoLoadAgent.email ?? '',
        MemberMobilePhone: '',
        MemberCity: '',
        MemberCountry: '',
      },
      eXpSystemKey: '',
      TeamLead: false,
    };

    void openAgentPlatformByData(syntheticAgent).then(() => {
      onAutoLoadDone?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadAgent?.uuid]);

  const openAgentPlatform = async () => {
    if (!selectedId) return;
    const selectedAgent = data.find((agent) => agent.Uuid === selectedId);
    if (!selectedAgent) return;
    await openAgentPlatformByData(selectedAgent);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const displayData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      data-testid="staff-data-table-container"
      sx={{
        mt: 4,
        overflow: 'hidden',
        borderRadius: '12px',
        width: '100%',
      }}
    >
      <Paper
        elevation={0}
        data-testid="staff-data-table-paper"
        sx={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          border: !loading
            ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
            : '',
          position: 'relative',
          width: '100%',
        }}
      >
        {loading && (
          <Box
            data-testid="staff-data-table-loading"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 10,
            }}
          >
            <CircularProgress
              color="primary"
              data-testid="staff-data-table-spinner"
            />
          </Box>
        )}

        {data.length === 0 && !loading && (
          <Box
            data-testid="staff-data-table-no-results"
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: '20px',
              gap: 2,
            }}
          >
            <SearchIcon
              sx={{
                fontSize: 50,
                color: alpha(theme.palette.text.primary, 0.3),
              }}
              data-testid="no-results-icon"
            />
            <Typography
              variant="body1"
              color="textSecondary"
              fontWeight={500}
              data-testid="no-results-message"
            >
              No results found. Try adjusting your search criteria.
            </Typography>
          </Box>
        )}

        {!loading && (
          <>
            <TableContainer
              data-testid="staff-data-table-container"
              sx={{
                maxHeight: 440,
                overflowX: 'auto',
                width: '100%',
              }}
            >
              <Table
                stickyHeader
                aria-label="staff table"
                size="small"
                data-testid="staff-data-table"
                sx={{ width: '100%' }}
              >
                <TableHead data-testid="staff-data-table-header">
                  <TableRow
                    data-testid="staff-data-table-header-row"
                    sx={{
                      '& th': {
                        backgroundColor: theme.palette.background.paper,
                        fontWeight: 'bold',
                        py: 1.5,
                      },
                    }}
                  >
                    <TableCell
                      data-testid="actions-header"
                      sx={{
                        width: '60px',
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600 }}
                      data-testid="first-name-header"
                    >
                      First Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600 }}
                      data-testid="last-name-header"
                    >
                      Last Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '80px' }}
                      data-testid="agent-id-header"
                    >
                      Agent ID
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '220px' }}
                      data-testid="uuid-header"
                    >
                      UUID
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '220px' }}
                      data-testid="email-header"
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '100px' }}
                      data-testid="status-header"
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '120px' }}
                      data-testid="team-type-header"
                    >
                      Team Type
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '150px' }}
                      data-testid="team-name-header"
                    >
                      Team Name
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '100px' }}
                      data-testid="team-lead-header"
                    >
                      Team Lead
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '80px' }}
                      data-testid="state-header"
                    >
                      State
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: '80px' }}
                      data-testid="country-header"
                    >
                      Country
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-testid="staff-data-table-body">
                  {displayData.map((row, index) => (
                    <TableRow
                      key={row.Uuid}
                      data-testid={`staff-data-row-${index}`}
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: alpha(
                            theme.palette.background.default,
                            0.5,
                          ),
                        },
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05,
                          ),
                        },
                      }}
                    >
                      <TableCell
                        align="center"
                        data-testid={`actions-cell-${index}`}
                      >
                        <IconButton
                          size="small"
                          onClick={(event) =>
                            handleActionClick(event, row.Uuid)
                          }
                          aria-label="actions"
                          data-testid={`actions-button-${index}`}
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1,
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.2,
                              ),
                            },
                            padding: '4px',
                          }}
                        >
                          <MoreVertIcon
                            fontSize="small"
                            color="primary"
                            data-testid={`actions-icon-${index}`}
                          />
                        </IconButton>
                      </TableCell>
                      <TableCell data-testid={`first-name-cell-${index}`}>
                        {row.Member.MemberFirstName}
                      </TableCell>
                      <TableCell data-testid={`last-name-cell-${index}`}>
                        {row.Member.MemberLastName}
                      </TableCell>
                      <TableCell data-testid={`agent-id-cell-${index}`}>
                        {row.SourceSystemMemberKey}
                      </TableCell>
                      <TableCell data-testid={`uuid-cell-${index}`}>
                        <Typography
                          variant="body2"
                          data-testid={`uuid-text-${index}`}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            maxWidth: '200px',
                          }}
                        >
                          {row.Uuid}
                        </Typography>
                      </TableCell>
                      <TableCell data-testid={`email-cell-${index}`}>
                        <Typography
                          variant="body2"
                          data-testid={`email-text-${index}`}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: theme.palette.primary.main,
                            maxWidth: '200px',
                          }}
                        >
                          {row.Member.MemberEmail}
                        </Typography>
                      </TableCell>
                      <TableCell data-testid={`status-cell-${index}`}>
                        <Box
                          data-testid={`status-badge-${index}`}
                          sx={{
                            backgroundColor:
                              row.status === 'Active'
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.text.disabled, 0.1),
                            color:
                              row.status === 'Active'
                                ? theme.palette.success.dark
                                : theme.palette.text.disabled,
                            borderRadius: '4px',
                            px: 1.5,
                            py: 0.5,
                            display: 'inline-block',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textAlign: 'center',
                            minWidth: '70px',
                          }}
                        >
                          {row.Member.MemberStatus}
                        </Box>
                      </TableCell>
                      <TableCell data-testid={`team-name-cell-${index}`}>
                        {row.TeamName || '-'}
                      </TableCell>
                      <TableCell data-testid={`team-type-cell-${index}`}>
                        {row.TeamType || '-'}
                      </TableCell>
                      <TableCell data-testid={`team-lead-cell-${index}`}>
                        {row.teamLead ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell data-testid={`state-cell-${index}`}>
                        {row.Member.MemberStateOrProvince}
                      </TableCell>
                      <TableCell data-testid={`country-cell-${index}`}>
                        {row.Member.MemberCountry}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              data-testid="staff-data-table-pagination"
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            />
          </>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        data-testid="staff-actions-menu"
        sx={{
          '& .MuiPaper-root': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            minWidth: '150px',
          },
        }}
      >
        <MenuItem
          onClick={openAgentPlatform}
          data-testid="agent-platform-menu-item"
        >
          Agent Platform
        </MenuItem>
        {platformLoading && (
          <Box
            data-testid="platform-loading-overlay"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress
              size={48}
              color="primary"
              data-testid="platform-loading-spinner"
            />
          </Box>
        )}
        {/* <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={handleEdit}>Edit</MenuItem> */}
      </Menu>
    </Box>
  );
};

export default StaffDataTable;
